import { Injectable } from '@angular/core';
import { multiVaultAbi } from '../abi/multiVault.abi';
import { multiVaultAbiV4 } from '../abi/multiVaultV4.abi';
import { VaultContractTypeEnum } from '@shared/enums/vault-contract-type.enum';
import { receiverVaultAbi } from '../abi/receiverVault.abi';
import { NetworkChainIdEnum } from '@shared/enums/networks.enum';
import { BehaviorSubject, from, Observable, shareReplay } from 'rxjs';
import { erc20abi } from '../abi/erc20.abi';
import { TokenInterface } from '@shared/interfaces/token.interface';
import { parseUnits, toBeHex, BrowserProvider, Contract } from 'ethers';
import { Loader } from '@shared/modules/custom-loader/models/decorators';
import { reconnect } from '@wagmi/core';
import { AppKit, createAppKit } from '@reown/appkit';
import {
    arbitrum,
    base,
    bsc,
    mainnet,
    polygon,
    solana,
    soneium,
    sonic,
} from '@reown/appkit/networks';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { AppKitNetwork } from '@reown/appkit-common/dist/types/src/utils/TypeUtil';

declare const window: any;

@Injectable()
export class MetamaskService {
    provider: any;
    appProvider: any;
    walletAddress: string | null;
    chainId;
    private readonly connectedMetamaskAddress = new BehaviorSubject<string | null>(null);
    private readonly currentChain = new BehaviorSubject<NetworkChainIdEnum | null>(null);
    modal: AppKit;
    projectId: string;

    constructor() {}

    initConnectWallet() {
        this.projectId = 'd0dfbbe66ddc57d64712c36aad1ef1bd';
        const chains: AppKitNetwork[] = [
            mainnet,
            bsc,
            polygon,
            arbitrum,
            base,
            solana,
            soneium,
            sonic,
        ];

        const metadata = {
            name: 'AppKit',
            description: 'AppKit Exampl',
            url: 'https://example.com',
            icons: ['https://avatars.githubusercontent.com/u/179229932'],
        };

        const wagmiAdapter = new WagmiAdapter({
            networks: chains,
            projectId: this.projectId,
        });
        const config = wagmiAdapter.wagmiConfig;

        if (config.state?.chainId) {
            reconnect(config);
        }

        this.modal = createAppKit({
            adapters: [wagmiAdapter],
            networks: [chains[0], ...chains.slice(1)],
            metadata: metadata,
            projectId: this.projectId,
            features: {
                analytics: true,
            },
            chainImages: {
                1868: 'assets/icons/networks/soneium.png',
                146: 'assets/icons/networks/sonic.png',
            },
            themeVariables: {
                '--w3m-border-radius-master': '3px',
            },
        });

        this.modal.subscribeAccount((newState) => {
            const chainId = newState?.caipAddress?.split(':')?.[1];
            if (
                newState?.isConnected &&
                chainId &&
                (chainId !== this.chainId || newState?.address !== this.walletAddress)
            ) {
                this.walletAddress = newState.address;
                this.connectedMetamaskAddress.next(this.walletAddress);
                this.chainId = chainId;
                this.currentChain.next(+chainId as NetworkChainIdEnum);

                if (!this.provider) {
                    this.connectProvider();
                }
            } else if (!chainId) {
                this.walletAddress = null;
                this.connectedMetamaskAddress.next(null);
                this.chainId = null;
                this.currentChain.next(this.chainId);
            }
        });
    }

    getConnectedAddress() {
        return this.connectedMetamaskAddress.asObservable().pipe(shareReplay(1));
    }

    getConnectedChain() {
        return this.currentChain.asObservable().pipe(shareReplay(1));
    }

    async connectProvider() {
        if (window.ethereum) {
            this.appProvider = window.ethereum;
            this.provider = new BrowserProvider(this.appProvider);
        } else {
            this.appProvider = this.modal.getWalletProvider();
            this.provider = new BrowserProvider(this.appProvider);
        }
    }

    async sendTransaction(addressTo: string, amount: string) {
        return this.provider
            .getGasPrice()
            .then((gasPrice) => {
                const value = toBeHex(parseUnits(amount, 18));
                return {
                    gasPrice: toBeHex(gasPrice),
                    gas: BigInt(
                        this.chainId === NetworkChainIdEnum.arbitrum ? '300000' : '21000',
                    ).toString(),
                    to: addressTo,
                    from: this.walletAddress,
                    value,
                };
            })
            .then((tx) => {
                return this.appProvider.request({
                    method: 'eth_sendTransaction',
                    params: [tx],
                });
            });
    }

    async sendTokens(addressTo: string, value: string, token: TokenInterface) {
        const signer = this.provider.getSigner();
        const fnAbi = erc20abi.filter((p) => p.name === 'transfer');
        const erc20 = new Contract(token.address, fnAbi, signer);
        const amount = parseUnits(value, token.decimals);
        return this.provider.getGasPrice().then((gasPrice) => {
            return erc20['transfer'](addressTo, amount, {
                gasPrice,
                gasLimit: '300000',
            });
        });
    }

    async withdrawEth(
        contractAddress: string | null,
        receiverAddress: string,
        type: VaultContractTypeEnum,
    ): Promise<any> {
        if (!contractAddress) {
            return;
        }
        const signer = this.provider.getSigner();
        const abi = type === VaultContractTypeEnum.receiver ? receiverVaultAbi : multiVaultAbi;
        const fnAbi = abi.filter((p) => p.name === 'withdrawETH');
        const contract = new Contract(contractAddress, fnAbi, signer);

        return this.provider.getGasPrice().then((gasPrice) => {
            if (window.ethereum) {
                return contract['withdrawETH'](receiverAddress, {
                    gasPrice: gasPrice,
                    gasLimit: '300000',
                });
            }
            contract['withdrawETH'](receiverAddress, {
                gasPrice: gasPrice,
                gasLimit: '300000',
            });
            return true;
        });
    }

    async withdrawToken(
        contractAddress: string | null,
        tokenAddress: string,
        receiverAddress: string,
        type: VaultContractTypeEnum,
    ): Promise<any> {
        if (!contractAddress) {
            return;
        }
        const signer = this.provider.getSigner();
        const abi = type === VaultContractTypeEnum.receiver ? receiverVaultAbi : multiVaultAbi;
        const fnAbi = abi.filter((p) => p.name === 'withdrawTokens');
        const contract = new Contract(contractAddress, fnAbi, signer);

        return this.provider.getGasPrice().then((gasPrice) => {
            if (window.ethereum) {
                return contract['withdrawTokens'](tokenAddress, receiverAddress, {
                    gasPrice: gasPrice,
                    gasLimit: '300000',
                });
            }
            contract['withdrawTokens'](tokenAddress, receiverAddress, {
                gasPrice: gasPrice,
                gasLimit: '300000',
            });
            return true;
        });
    }

    @Loader()
    setMainReceiver(newReceiver: string, contractAddress: string | null): Observable<any> {
        if (!this.walletAddress) {
            throw new Error('To change main receiver you need connect to Metamask wallet first');
        }
        const signer = this.provider.getSigner();
        const contract = new Contract(contractAddress, multiVaultAbiV4, signer);
        return from(
            this.provider.getGasPrice().then((gasPrice) => {
                if (window.ethereum) {
                    return contract['setReceiver'](newReceiver, {
                        from: this.walletAddress,
                        gasPrice: gasPrice,
                        gasLimit: '300000',
                    }).then((tx) => tx.wait(1));
                }
                contract['setReceiver'](newReceiver, {
                    from: this.walletAddress,
                    gasPrice: gasPrice,
                    gasLimit: '300000',
                });
                return true;
            }),
        );
    }
}
